(() => {
  if (window.HederaCollectionFiltersLoaded) return;
  window.HederaCollectionFiltersLoaded = true;

  const selector = '[data-hdc-filter-root]';
  const requestStates = new Map();

  const getRoot = (element) => element && element.closest(selector);

  const getState = (root) => {
    const sectionId = root.dataset.sectionId;
    if (!requestStates.has(sectionId)) {
      requestStates.set(sectionId, { controller: null, timer: null, previousOverflow: '' });
    }
    return requestStates.get(sectionId);
  };

  const stopPendingRequest = (root) => {
    const state = getState(root);
    window.clearTimeout(state.timer);
    state.timer = null;
    if (state.controller) {
      state.controller.abort();
      state.controller = null;
    }
  };

  const captureInterfaceState = (root) => ({
    dialogOpen: Boolean(root.querySelector('.hdc-filter-dialog[open]')),
    focusedId: document.activeElement && root.contains(document.activeElement) ? document.activeElement.id : '',
    openFilters: Array.from(root.querySelectorAll('.hdc-filter[open]')).map((details) => ({
      form: details.closest('form')?.classList.contains('hdc-filters-form--mobile') ? 'mobile' : 'desktop',
      index: details.dataset.filterIndex
    }))
  });

  const restoreInterfaceState = (root, state) => {
    root.querySelectorAll('.hdc-filter[open]').forEach((details) => {
      details.open = false;
    });

    state.openFilters.forEach(({ form, index }) => {
      const formSelector = form === 'mobile' ? '.hdc-filters-form--mobile' : '.hdc-filters-form--desktop';
      const details = root.querySelector(`${formSelector} .hdc-filter[data-filter-index="${CSS.escape(index)}"]`);
      if (details) details.open = true;
    });

    if (state.dialogOpen) {
      const dialog = root.querySelector('.hdc-filter-dialog');
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      document.body.style.overflow = 'hidden';
    }

    if (state.focusedId) {
      const focusedElement = root.querySelector(`#${CSS.escape(state.focusedId)}`);
      if (focusedElement) focusedElement.focus({ preventScroll: true });
    }
  };

  const replaceSection = (root, html) => {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextRoot = parsedDocument.querySelector(`#${CSS.escape(root.id)}`);
    if (!nextRoot) throw new Error('Filtered collection section was not found');

    const interfaceState = captureInterfaceState(root);
    root.replaceWith(nextRoot);
    restoreInterfaceState(nextRoot, interfaceState);
    return nextRoot;
  };

  const requestSection = async (root, targetUrl, updateHistory = true) => {
    const state = getState(root);
    if (state.controller) state.controller.abort();
    const controller = new AbortController();
    state.controller = controller;

    root.classList.add('is-filtering');
    root.setAttribute('aria-busy', 'true');

    const requestUrl = new URL(targetUrl.href);
    requestUrl.searchParams.set('section_id', root.dataset.sectionId);

    try {
      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);

      const html = await response.text();
      const nextRoot = replaceSection(root, html);

      if (updateHistory) {
        const visibleUrl = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
        window.history.pushState({ hederaFilters: true }, '', visibleUrl);
      }

      nextRoot.classList.remove('is-filtering');
      nextRoot.removeAttribute('aria-busy');
      document.dispatchEvent(new CustomEvent('hedera:collection-filtered', {
        detail: { sectionId: nextRoot.dataset.sectionId, url: targetUrl.toString() }
      }));
      return nextRoot;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      window.location.assign(targetUrl.toString());
      return null;
    } finally {
      if (state.controller === controller) state.controller = null;
    }
  };

  const urlFromForm = (form) => {
    const targetUrl = new URL(form.action, window.location.origin);
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    searchParams.delete('page');
    targetUrl.search = searchParams.toString();
    return targetUrl;
  };

  const closeDialog = (root) => {
    if (!root) return;
    const dialog = root.querySelector('.hdc-filter-dialog[open]');
    if (dialog) dialog.close();
    if (!dialog) {
      const state = getState(root);
      document.body.style.overflow = state.previousOverflow;
      root.querySelector('.hdc-mobile-filter-trigger')?.focus({ preventScroll: true });
    }
  };

  const scheduleFormUpdate = (form, delay = 300) => {
    const root = getRoot(form);
    if (!root) return;

    stopPendingRequest(root);
    const state = getState(root);
    state.timer = window.setTimeout(() => {
      state.timer = null;
      requestSection(root, urlFromForm(form));
    }, delay);
  };

  document.addEventListener('input', (event) => {
    const form = event.target.closest('.hdc-filters-form');
    if (!form || !getRoot(form)) return;

    const delay = event.target.matches('input[type="number"]') ? 500 : 250;
    scheduleFormUpdate(form, delay);
  });

  document.addEventListener('change', (event) => {
    const sortForm = event.target.closest('.hdc-sort');
    if (sortForm && getRoot(sortForm)) {
      scheduleFormUpdate(sortForm, 0);
      return;
    }

    const filterForm = event.target.closest('.hdc-filters-form');
    if (filterForm && event.target.matches('input[type="checkbox"]')) {
      scheduleFormUpdate(filterForm, 150);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.hdc-filters-form, .hdc-sort');
    const root = getRoot(form);
    if (!form || !root) return;

    event.preventDefault();
    const targetUrl = urlFromForm(form);
    const currentUrl = new URL(window.location.href);

    if (targetUrl.pathname === currentUrl.pathname && targetUrl.search === currentUrl.search) {
      closeDialog(root);
      return;
    }

    stopPendingRequest(root);
    requestSection(root, targetUrl).then((nextRoot) => {
      if (nextRoot) closeDialog(nextRoot);
    });
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.hdc-mobile-filter-trigger');
    if (trigger) {
      const root = getRoot(trigger);
      const dialog = root?.querySelector('.hdc-filter-dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      getState(root).previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.showModal();
      return;
    }

    const closeButton = event.target.closest('.hdc-filter-dialog__close');
    if (closeButton) {
      closeDialog(getRoot(closeButton));
      return;
    }

    const filterLink = event.target.closest('.hdc-active-filter, .hdc-active-filters__clear, .hdc-filter-clear');
    const root = getRoot(filterLink);
    if (!filterLink || !root || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    stopPendingRequest(root);
    requestSection(root, new URL(filterLink.href, window.location.origin));
  });

  document.addEventListener('click', (event) => {
    const dialog = event.target.closest('.hdc-filter-dialog');
    if (dialog && event.target === dialog) closeDialog(getRoot(dialog));
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target.closest('.hdc-filter-dialog');
    const root = getRoot(dialog);
    if (!dialog || !root) return;
    const state = getState(root);
    document.body.style.overflow = state.previousOverflow;
    root.querySelector('.hdc-mobile-filter-trigger')?.focus({ preventScroll: true });
  }, true);

  window.addEventListener('popstate', () => {
    const root = document.querySelector(selector);
    if (!root) return;
    stopPendingRequest(root);
    requestSection(root, new URL(window.location.href), false);
  });
})();
